import { useState } from "react";
import { useNavigate } from "react-router";
import AppButton from "../../../../../components/common/AppButton";
import AppTextInput from "../../../../../components/common/AppTextInput";
import { SVG } from "../../../../../constants/assetLists";
import { handleUpdateAdminLocationStatusService, OrganizationNode } from "../../../../../services/organizations";
import { isSuperAdminOnly } from "../../../../../utils";
import {
  COUNTRY_OPTIONS,
  getIsraelCitiesByState,
  ISRAEL_STATE_OPTIONS,
} from "../../../../on-boarding/components/NetworkDetails/data/israelLocations";
import AppSwitch from "../../../../../components/common/AppSwitch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import QUERY_KEYS from "../../../../../constants/queryKeys";
import { useSnackbar } from "../../../../../context/SnackbarContextProvider";
import { getStatusLabel } from "../../OrganizationsList/components/OrganizationsTable";

type Props = {
  organization: OrganizationNode;
};

const BRANCH_TYPE_OPTIONS = [
  { label: "מסעדה", value: "restaurant" },
  { label: "בית קפה", value: "cafe" },
  { label: "בר", value: "bar" },
  { label: "חנות", value: "store" },
  { label: "מאפיה", value: "bakery" },
  { label: "פיצריה", value: "pizzeria" },
  { label: "קבוצה", value: "group" },
] as const;

function ReadonlyOrganizationDetails({ organization }: Props) {
  const navigate = useNavigate();
  const [expandedSet, setExpandedSet] = useState<Set<string>>(
    new Set(["branch", "building", "contact"]),
  );

  const toggleExpand = (id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleNavigateToOnBoarding = () => {
    const draftId = organization.draftId;
    if (isSuperAdminOnly() && draftId) {
      navigate(`/on-boarding?draftId=${encodeURIComponent(draftId)}&step=2`);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto border border-gray70 rounded-lg px-4 py-3">
      <div className="flex flex-col gap-3">
        <ReadonlyBranchDetails
          organization={organization}
          expanded={expandedSet.has("branch")}
          onToggle={toggleExpand}
          onNavigateToOnBoarding={handleNavigateToOnBoarding}
        />

        <ReadonlyBuildingDetails
          organization={organization}
          expanded={expandedSet.has("building")}
          onToggle={toggleExpand}
        />

        <ReadonlyContactDetails
          organization={organization}
          expanded={expandedSet.has("contact")}
          onToggle={toggleExpand}
        />
      </div>
    </div>
  );
}

type SectionProps = {
  organization: OrganizationNode;
  expanded: boolean;
  onToggle: (id: string) => void;
};

function ReadonlyBranchDetails({
  organization,
  expanded,
  onToggle,
  onNavigateToOnBoarding,
}: SectionProps & { onNavigateToOnBoarding: () => void }) {
  const activeSectionText = getActiveSectionText(organization);
  const typeLabel =
    BRANCH_TYPE_OPTIONS.find((option) => option.value === organization.type)
      ?.label ??
    organization.type ??
    "";

  const queryClient = useQueryClient();
  
  const { showSnackbar } = useSnackbar();

  const updateStatusMutation = useMutation({
    mutationFn: handleUpdateAdminLocationStatusService,
    onSuccess: (data ) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ADMIN_ON_BOARDING_DRAFTS],
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT],
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_CLIENT],
      });

      showSnackbar({
        message: data?.message || 'הסטטוס עודכן בהצלחה',
        type: 'success',
      });
    },
  });

  const handleStatusChange = (org: OrganizationNode, checked: boolean) => {
    const nextStatus = checked ? 'active' : 'inactive';

    updateStatusMutation.mutate({
      locationType: org.isClient
        ? 'client'
        : org.isBranch
          ? 'branch'
          : 'subBranch',
      locationId: org.id,
      status: nextStatus,
    });
  };
  
  const isActive = organization.status === "active";
  const isDraft = organization.status === "draft";

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <SectionToggle
              expanded={expanded}
              sectionId="branch"
              onToggle={onToggle}
            />

            <div className="flex items-center gap-1">
              <span className="text-base font-semibold text-raspberryRed">
                *
              </span>
              <p className="text-base font-semibold text-textColor">
                פרטי {activeSectionText}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-5">
          {isDraft ? (
            <span className="flex-shrink-0 px-[8px] py-[4px] rounded-[6px] border-[1px] border-[#707070] bg-[#E5E5E5] text-[#707070] text-lg leading-4">
              טיוטה
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <AppSwitch
                checked={isActive}
                outlined={!isActive}
                disabled={
                  updateStatusMutation.isPending || !!organization.isArchived
                }
                onChange={event =>
                  handleStatusChange(organization, event.target.checked)
                }
              />
              <span>{getStatusLabel(organization.status)}</span>
            </div>
          )}

          <AppButton
            variant="custom"
            className="bg-linkBlue text-white hover:opacity-90"
            iconStart={
              <img
                src={SVG.editSettingsWhiteIcon}
                alt="edit"
                className="w-[15px] h-[15px] object-contain"
              />
            }
            onClick={onNavigateToOnBoarding}
          >
            הגדרת {activeSectionText}
          </AppButton>
        </div>
      </div>

      {expanded && (
        <div className="flex items-center gap-3">
          <div className="flex-1 flex flex-col gap-3">
            <ReadonlyField
              label={`שם ${activeSectionText}`}
              value={organization.name}
              required
            />

            <ReadonlyField
              label="סוג (מסעדה/בית קפה/בר/מאפיה/פיצריה/קבוצה...)"
              value={typeLabel}
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <ReadonlyLogoField
              logoUrl={organization.mobileLogo?.url}
              logoLabel="לוגו לאפליקציה על הסלולרי"
              onClick={onNavigateToOnBoarding}
            />
            <ReadonlyLogoField
              logoUrl={organization.logo?.url}
              logoLabel="לוגו לאפליקציה על המחשב"
              onClick={onNavigateToOnBoarding}
            />

            <span className="w-[147px] text-base text-textColor">
              יש להעלות קבצים בפורמט PNG, JPG בלבד.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function ReadonlyBuildingDetails({
  organization,
  expanded,
  onToggle,
}: SectionProps) {
  const countryLabel = getOptionLabel(COUNTRY_OPTIONS, organization.country);
  const stateLabel = getOptionLabel(ISRAEL_STATE_OPTIONS, organization.state);
  const cityLabel = getOptionLabel(
    organization.state ? getIsraelCitiesByState(organization.state) : [],
    organization.city,
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <SectionToggle
          expanded={expanded}
          sectionId="building"
          onToggle={onToggle}
        />

        <div className="flex items-center gap-1">
          <span className="text-base font-semibold text-raspberryRed">*</span>
          <p className="text-base font-semibold text-textColor">
            כתובת מטה חברה
          </p>
        </div>
      </div>

      {expanded && (
        <>
          <div className="flex gap-3">
            <ReadonlyField label="מדינה" value={countryLabel} required />
            <ReadonlyField label="אזור" value={stateLabel} required />
            <ReadonlyField label="עיר" value={cityLabel} required />
            <ReadonlyField
              label="כתובת"
              value={organization.address}
              required
            />
          </div>

          <div className="flex gap-3">
            <ReadonlyField label="מספר ח.פ." value={organization.hpNumber} />
            <ReadonlyField label="אתר אינטרנט" value={organization.website} />
          </div>

          <div className="flex gap-3">
            <ReadonlyField
              label="טלפון חברה"
              value={organization.phone}
              required
            />
            <ReadonlyField
              label="אימייל חברה"
              value={organization.email}
              required
            />
          </div>
        </>
      )}
    </div>
  );
}

function ReadonlyContactDetails({
  organization,
  expanded,
  onToggle,
}: SectionProps) {
  const contacts = organization.contacts ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <SectionToggle
          expanded={expanded}
          sectionId="contact"
          onToggle={onToggle}
        />

        <div className="flex items-center gap-1">
          <span className="text-base font-semibold text-raspberryRed">*</span>
          <p className="text-base font-semibold text-textColor">אנשי קשר</p>
        </div>
      </div>

      {expanded &&
        contacts.map((contact, index) => {
          const isRequired = index === 0;

          return (
            <div className="flex gap-3" key={`${contact.email}-${index}`}>
              <ReadonlyField
                label="שם"
                value={contact.firstName}
                required={isRequired}
              />
              <ReadonlyField
                label="שם משפחה"
                value={contact.lastName}
                required={isRequired}
              />
              <ReadonlyField
                label="תפקיד בארגון"
                value={contact.role}
                required={isRequired}
              />
              <ReadonlyField
                label="טלפון"
                value={contact.phone}
                required={isRequired}
              />
              <ReadonlyField
                label="אימייל"
                value={contact.email}
                required={isRequired}
              />
            </div>
          );
        })}
    </div>
  );
}

function SectionToggle({
  expanded,
  sectionId,
  onToggle,
}: {
  expanded: boolean;
  sectionId: string;
  onToggle: (id: string) => void;
}) {
  return (
    <button
      type="button"
      className="w-[12px] h-[12px]"
      onClick={() => onToggle(sectionId)}
    >
      <img
        src={expanded ? SVG.expandIcon : SVG.lessThanIcon}
        alt="caret"
        className="w-full h-full object-contain"
      />
    </button>
  );
}

function ReadonlyField({
  label,
  value,
  required,
}: {
  label: string;
  value?: string | null;
  required?: boolean;
}) {
  return (
    <div className="flex-1">
      <AppTextInput
        label={label}
        placeholder={label}
        className="border-none"
        value={value ?? ""}
        readOnly
        required={required}
      />
    </div>
  );
}

function ReadonlyLogoField({
  logoUrl,
  logoLabel,
  onClick,
}: {
  logoUrl?: string;
  logoLabel: string;
  onClick: () => void;
}) {
  return (
    <div className="relative w-[202px] h-[116px] rounded-lg border border-gray90 bg-gray90 flex items-center justify-center overflow-hidden">
      {logoUrl ? (
        <>
          <img
            src={logoUrl}
            alt={logoLabel}
            className="w-full h-full object-contain rounded-lg"
          />

          <button
            type="button"
            onClick={onClick}
            className="absolute bottom-0.5 left-0 right-0 bg-[#007AFF] text-white text-sm text-center max-w-fit mx-auto px-2 py-0.5 rounded-[10px] cursor-pointer"
          >
            החלפה
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
        >
          <div className="w-[80px] h-[80px] pointer-events-none">
            <img
              src={SVG.imgPlaceholderWhiteIcon}
              alt="icon"
              className="w-full h-full object-contain"
            />
          </div>

          <span className="absolute left-50% top-50% transform -translate-x-50% -translate-y-50% text-base text-center text-textColor pointer-events-none">
            {logoLabel}
          </span>
        </button>
      )}
    </div>
  );
}

function getOptionLabel(
  options: readonly { label: string; value: string }[],
  value?: string,
) {
  return options.find((option) => option.value === value)?.label ?? value ?? "";
}

function getActiveSectionText(organization: OrganizationNode) {
  if (organization.isClient) return "רשת";
  if (organization.isBranch) return "סניף";
  return "תת סניף";
}

export default ReadonlyOrganizationDetails;
